/**
 * HTTP client utility with error handling and type safety
 */

import { config } from '@/config';
import type { ApiError, ApiResponse } from '@/types';

/**
 * HTTP methods supported by the client
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Request options for HTTP client
 */
export interface RequestOptions<TBody = unknown> {
    method?: HttpMethod;
    body?: TBody;
    headers?: Record<string, string>;
    signal?: AbortSignal;
}

/**
 * Custom HTTP error class
 */
export class HttpError extends Error {
    constructor(
        public readonly status: number,
        public readonly statusText: string,
        public readonly apiError?: ApiError
    ) {
        super(apiError?.message || statusText);
        this.name = 'HttpError';
    }

    public get statusCode(): number {
        return this.status;
    }
}

/**
 * Network error class
 */
export class NetworkError extends Error {
    constructor(message: string = 'Network error occurred') {
        super(message);
        this.name = 'NetworkError';
    }
}

/**
 * Timeout error class
 */
export class TimeoutError extends Error {
    constructor(message: string = 'Request timeout') {
        super(message);
        this.name = 'TimeoutError';
    }
}

/**
 * Build full URL from endpoint
 */
const buildUrl = (endpoint: string): string => {
    const baseUrl = config.api.baseUrl.replace(/\/$/, '');
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${baseUrl}${cleanEndpoint}`;
};

/**
 * Create abort controller with timeout
 */
const createAbortController = (
    timeout: number,
    externalSignal?: AbortSignal
): { controller: AbortController; timeoutId: ReturnType<typeof setTimeout> } => {
    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
        controller.abort();
    }, timeout);

    if (externalSignal) {
        externalSignal.addEventListener('abort', () => {
            clearTimeout(timeoutId);
            controller.abort();
        });
    }

    return { controller, timeoutId };
};

/**
 * Parse response body
 */
const parseResponse = async <T>(response: Response): Promise<T> => {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
        return response.json() as Promise<T>;
    }

    const text = await response.text();
    return text as unknown as T;
};

/**
 * HTTP client for making API requests
 */
export const httpClient = {
    /**
     * Make an HTTP request
     */
    async request<TResponse, TBody = unknown>(
        endpoint: string,
        options: RequestOptions<TBody> = {}
    ): Promise<TResponse> {
        const { method = 'GET', body, headers = {}, signal } = options;
        const url = buildUrl(endpoint);

        const { controller, timeoutId } = createAbortController(
            config.api.timeout,
            signal
        );

        const requestHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...headers,
        };

        try {
            const response = await fetch(url, {
                method,
                headers: requestHeaders,
                body: body ? JSON.stringify(body) : undefined,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                let apiError: ApiError | undefined;
                try {
                    const errorResponse = await parseResponse<ApiResponse<unknown>>(response);
                    apiError = errorResponse.error;
                } catch {
                    // Unable to parse error response
                }
                throw new HttpError(response.status, response.statusText, apiError);
            }

            return parseResponse<TResponse>(response);
        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof HttpError) {
                throw error;
            }

            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new TimeoutError();
                }
                throw new NetworkError(error.message);
            }

            throw new NetworkError();
        }
    },

    /**
     * GET request
     */
    get<TResponse>(
        endpoint: string,
        options?: Omit<RequestOptions, 'method' | 'body'>
    ): Promise<TResponse> {
        return this.request<TResponse>(endpoint, { ...options, method: 'GET' });
    },

    /**
     * POST request
     */
    post<TResponse, TBody = unknown>(
        endpoint: string,
        body: TBody,
        options?: Omit<RequestOptions, 'method' | 'body'>
    ): Promise<TResponse> {
        return this.request<TResponse, TBody>(endpoint, { ...options, method: 'POST', body });
    },

    /**
     * PUT request
     */
    put<TResponse, TBody = unknown>(
        endpoint: string,
        body: TBody,
        options?: Omit<RequestOptions, 'method' | 'body'>
    ): Promise<TResponse> {
        return this.request<TResponse, TBody>(endpoint, { ...options, method: 'PUT', body });
    },

    /**
     * PATCH request
     */
    patch<TResponse, TBody = unknown>(
        endpoint: string,
        body: TBody,
        options?: Omit<RequestOptions, 'method' | 'body'>
    ): Promise<TResponse> {
        return this.request<TResponse, TBody>(endpoint, { ...options, method: 'PATCH', body });
    },

    /**
     * DELETE request
     */
    delete<TResponse>(
        endpoint: string,
        options?: Omit<RequestOptions, 'method' | 'body'>
    ): Promise<TResponse> {
        return this.request<TResponse>(endpoint, { ...options, method: 'DELETE' });
    },
};

export default httpClient;
